import React, { useState, useEffect } from 'react';
import { 
  Search, Loader2, Plus, Printer, Ban, Eye, FileText,
  ChevronLeft, ChevronRight, X, Calendar, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { checksAPI, bankAccountsAPI, paymentsAPI } from '../../utils/api';
import { toast } from 'sonner';

const formatCents = (cents) => {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-yellow-100 text-yellow-800',
    printed: 'bg-green-100 text-green-800',
    void: 'bg-red-100 text-red-800',
  };
  const labels = {
    draft: '📝 Draft',
    printed: '🖨️ Printed',
    void: '🚫 Void',
  };
  return (
    <Badge className={styles[status] || 'bg-gray-100'}>
      {labels[status] || status}
    </Badge>
  );
};

const AdminChecks = () => {
  const [checks, setChecks] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBankAccount, setFilterBankAccount] = useState('');
  const [searchPayee, setSearchPayee] = useState('');
  
  // Modals
  const [showNewCheckModal, setShowNewCheckModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  
  // New Check Form
  const [formData, setFormData] = useState({
    bank_account_id: '',
    date: new Date().toISOString().split('T')[0],
    payee_name: '',
    payee_address: '',
    amount: '',
    memo: '',
    source: 'manual', // 'manual' or 'payment'
    payment_id: '',
  });
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [nextCheckNumber, setNextCheckNumber] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Void form
  const [voidReason, setVoidReason] = useState('');

  // Fetch checks
  const fetchChecks = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filterStatus) params.status = filterStatus;
      if (filterBankAccount) params.bank_account_id = filterBankAccount;
      if (searchPayee) params.payee = searchPayee;
      
      const res = await checksAPI.getChecks(params);
      setChecks(res.checks || []);
      setTotalPages(res.total_pages || 1);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error('Failed to load checks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch bank accounts
  const fetchBankAccounts = async () => {
    try {
      const res = await bankAccountsAPI.getAccounts();
      setBankAccounts(res || []);
      // Set default bank account
      const defaultAccount = res.find(a => a.is_default);
      if (defaultAccount && !formData.bank_account_id) {
        setFormData(prev => ({ ...prev, bank_account_id: defaultAccount.id }));
        setNextCheckNumber(defaultAccount.next_check_number);
      }
    } catch (err) {
      console.error('Failed to fetch bank accounts', err);
    }
  };

  // Fetch pending check payments
  const fetchPendingPayments = async () => {
    setLoadingPayments(true);
    try {
      const res = await paymentsAPI.getPayments({ 
        payment_type: 'expense',
        payment_method: 'check',
        status: 'posted',
        page_size: 100 
      });
      // Filter out payments that already have checks
      const existingPaymentIds = checks.map(c => c.payment_id).filter(Boolean);
      const pending = (res.payments || []).filter(p => !existingPaymentIds.includes(p.id));
      setPendingPayments(pending);
    } catch (err) {
      console.error('Failed to fetch pending payments', err);
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    fetchChecks();
    fetchBankAccounts();
  }, [page, filterStatus, filterBankAccount]);

  useEffect(() => {
    if (searchPayee) {
      const timer = setTimeout(() => fetchChecks(), 300);
      return () => clearTimeout(timer);
    }
  }, [searchPayee]);

  // Handle bank account change - update next check number
  const handleBankAccountChange = async (accountId) => {
    setFormData(prev => ({ ...prev, bank_account_id: accountId }));
    try {
      const res = await bankAccountsAPI.getNextCheckNumber(accountId);
      setNextCheckNumber(res.next_check_number);
    } catch (err) {
      console.error('Failed to get next check number', err);
    }
  };

  // Handle payment selection
  const handlePaymentSelect = async (paymentId) => {
    if (!paymentId) {
      setFormData(prev => ({
        ...prev,
        payment_id: '',
        payee_name: '',
        payee_address: '',
        amount: '',
        memo: '',
      }));
      return;
    }

    try {
      const preview = await checksAPI.previewFromPayment(paymentId);
      setFormData(prev => ({
        ...prev,
        payment_id: paymentId,
        payee_name: preview.payee_name || '',
        payee_address: preview.payee_address || '',
        amount: (preview.amount_cents / 100).toFixed(2),
        memo: preview.memo || '',
      }));
    } catch (err) {
      toast.error('Failed to load payment details');
      console.error(err);
    }
  };

  // Open new check modal
  const openNewCheckModal = () => {
    setFormData({
      bank_account_id: bankAccounts.find(a => a.is_default)?.id || '',
      date: new Date().toISOString().split('T')[0],
      payee_name: '',
      payee_address: '',
      amount: '',
      memo: '',
      source: 'manual',
      payment_id: '',
    });
    fetchPendingPayments();
    setShowNewCheckModal(true);
  };

  // Create check
  const handleCreateCheck = async (printNow = false) => {
    if (!formData.bank_account_id) {
      toast.error('Please select a bank account');
      return;
    }
    if (!formData.payee_name) {
      toast.error('Please enter payee name');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        bank_account_id: formData.bank_account_id,
        date: formData.date,
        payee_name: formData.payee_name,
        payee_address: formData.payee_address || null,
        amount_cents: Math.round(parseFloat(formData.amount) * 100),
        memo: formData.memo || null,
        payment_id: formData.source === 'payment' ? formData.payment_id : null,
        print_now: printNow,
      };

      const newCheck = await checksAPI.createCheck(payload);
      toast.success(`Check #${newCheck.check_number} ${printNow ? 'created and printed' : 'saved as draft'}`);
      
      if (printNow) {
        // Download PDF
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/checks/${newCheck.id}/pdf`, '_blank');
      }
      
      setShowNewCheckModal(false);
      fetchChecks();
      fetchBankAccounts(); // Refresh next check number
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create check');
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  // Print check
  const handlePrintCheck = async (check) => {
    try {
      if (check.status === 'draft') {
        await checksAPI.printCheck(check.id);
      }
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/checks/${check.id}/pdf`, '_blank');
      fetchChecks();
    } catch (err) {
      toast.error('Failed to print check');
      console.error(err);
    }
  };

  // Void check
  const handleVoidCheck = async () => {
    if (!voidReason.trim()) {
      toast.error('Please enter a reason for voiding');
      return;
    }

    try {
      await checksAPI.voidCheck(selectedCheck.id, voidReason);
      toast.success(`Check #${selectedCheck.check_number} voided`);
      setShowVoidModal(false);
      setVoidReason('');
      fetchChecks();
    } catch (err) {
      toast.error('Failed to void check');
      console.error(err);
    }
  };

  // View check details
  const handleViewCheck = async (check) => {
    setSelectedCheck(check);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-checks-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Checks</h1>
          <p className="text-gray-500">Manage and print checks for vendor payments</p>
        </div>
        <Button onClick={openNewCheckModal} data-testid="new-check-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Check
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="w-48">
          <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="printed">Printed</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={filterBankAccount || 'all'} onValueChange={(v) => setFilterBankAccount(v === 'all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Bank Accounts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bank Accounts</SelectItem>
              {bankAccounts.map(acc => (
                <SelectItem key={acc.id} value={acc.id}>
                  {acc.account_name} ****{acc.account_number_last4}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 max-w-xs">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search payee..."
              value={searchPayee}
              onChange={(e) => setSearchPayee(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Checks Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : checks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No checks found. Create your first check to get started.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payee</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {checks.map((check) => (
                <tr key={check.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{check.check_number}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(check.date)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{check.payee_name}</div>
                    {check.vendor_name && (
                      <div className="text-xs text-gray-500">{check.vendor_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{formatCents(check.amount_cents)}</td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={check.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => handleViewCheck(check)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {check.status !== 'void' && (
                        <Button variant="ghost" size="sm" onClick={() => handlePrintCheck(check)}>
                          <Printer className="w-4 h-4" />
                        </Button>
                      )}
                      {check.status !== 'void' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setSelectedCheck(check);
                            setShowVoidModal(true);
                          }}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-4 py-3 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              Showing {checks.length} of {total} checks
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm">
                Page {page} of {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* New Check Modal */}
      <Dialog open={showNewCheckModal} onOpenChange={setShowNewCheckModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Check</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Check Number & Bank Account */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check Number</Label>
                <Input value={nextCheckNumber || 'Auto'} disabled className="bg-gray-50" />
              </div>
              <div>
                <Label>Bank Account *</Label>
                <Select 
                  value={formData.bank_account_id} 
                  onValueChange={handleBankAccountChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.account_name} ****{acc.account_number_last4}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Source Selection */}
            <div>
              <Label>Source</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="source"
                    value="payment"
                    checked={formData.source === 'payment'}
                    onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
                    className="w-4 h-4"
                  />
                  <span>From Payment</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="source"
                    value="manual"
                    checked={formData.source === 'manual'}
                    onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value, payment_id: '' }))}
                    className="w-4 h-4"
                  />
                  <span>Manual Entry</span>
                </label>
              </div>
            </div>

            {/* Payment Selection (if from payment) */}
            {formData.source === 'payment' && (
              <div>
                <Label>Select Payment</Label>
                <Select 
                  value={formData.payment_id} 
                  onValueChange={handlePaymentSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a payment..." />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingPayments ? (
                      <div className="p-2 text-center text-gray-500">Loading...</div>
                    ) : pendingPayments.length === 0 ? (
                      <div className="p-2 text-center text-gray-500">No pending check payments</div>
                    ) : (
                      pendingPayments.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.payment_number} - {p.vendor_name || 'Unknown'} - {formatCents(p.amount_cents)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {formData.payment_id && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Payee, amount & memo auto-filled from payment
                  </p>
                )}
              </div>
            )}

            {/* Payee Info */}
            <div>
              <Label>Payee Name *</Label>
              <Input
                value={formData.payee_name}
                onChange={(e) => setFormData(prev => ({ ...prev, payee_name: e.target.value }))}
                placeholder="Enter payee name"
              />
            </div>

            <div>
              <Label>Payee Address</Label>
              <Textarea
                value={formData.payee_address}
                onChange={(e) => setFormData(prev => ({ ...prev, payee_address: e.target.value }))}
                placeholder="Address (optional)"
                rows={2}
              />
            </div>

            {/* Amount & Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className="pl-7"
                    placeholder="0.00"
                    disabled={formData.source === 'payment' && formData.payment_id}
                  />
                </div>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
            </div>

            {/* Memo */}
            <div>
              <Label>Memo</Label>
              <Input
                value={formData.memo}
                onChange={(e) => setFormData(prev => ({ ...prev, memo: e.target.value }))}
                placeholder="Memo (appears on check)"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNewCheckModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleCreateCheck(false)}
              disabled={formLoading}
            >
              Save Draft
            </Button>
            <Button 
              onClick={() => handleCreateCheck(true)}
              disabled={formLoading}
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Printer className="w-4 h-4 mr-2" />}
              Print Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Check #{selectedCheck?.check_number}</DialogTitle>
          </DialogHeader>
          
          {selectedCheck && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between">
                <StatusBadge status={selectedCheck.status} />
                <span className="text-2xl font-bold">{formatCents(selectedCheck.amount_cents)}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Date:</span>
                  <p className="font-medium">{formatDate(selectedCheck.date)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Bank Account:</span>
                  <p className="font-medium">{selectedCheck.bank_account_name}</p>
                </div>
              </div>

              <div>
                <span className="text-gray-500 text-sm">Payee:</span>
                <p className="font-medium">{selectedCheck.payee_name}</p>
                {selectedCheck.payee_address && (
                  <p className="text-sm text-gray-600 whitespace-pre-line">{selectedCheck.payee_address}</p>
                )}
              </div>

              <div>
                <span className="text-gray-500 text-sm">Amount in Words:</span>
                <p className="font-medium">{selectedCheck.amount_words} DOLLARS</p>
              </div>

              {selectedCheck.memo && (
                <div>
                  <span className="text-gray-500 text-sm">Memo:</span>
                  <p className="font-medium">{selectedCheck.memo}</p>
                </div>
              )}

              {selectedCheck.line_items?.length > 0 && (
                <div>
                  <span className="text-gray-500 text-sm">Bills/POs Included:</span>
                  <div className="mt-2 border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Invoice</th>
                          <th className="px-3 py-2 text-left">PO</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {selectedCheck.line_items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{item.bill_number || '-'}</td>
                            <td className="px-3 py-2">{item.po_number || '-'}</td>
                            <td className="px-3 py-2 text-right">{formatCents(item.amount_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedCheck.status === 'void' && (
                <div className="bg-red-50 p-3 rounded-lg">
                  <span className="text-red-800 font-medium">Void Reason:</span>
                  <p className="text-red-700">{selectedCheck.void_reason}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedCheck?.status !== 'void' && (
              <Button variant="outline" onClick={() => handlePrintCheck(selectedCheck)}>
                <Printer className="w-4 h-4 mr-2" />
                {selectedCheck?.status === 'draft' ? 'Print' : 'Reprint'}
              </Button>
            )}
            <Button onClick={() => setShowDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Modal */}
      <Dialog open={showVoidModal} onOpenChange={setShowVoidModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Check #{selectedCheck?.check_number}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              Are you sure you want to void this check? This action cannot be undone.
            </p>
            <Label>Reason for voiding *</Label>
            <Textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Enter reason..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoidModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleVoidCheck}>
              Void Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChecks;
